import { useState } from 'react';
import Table from 'components/Table';
import { numberToCurrencyString, dateToLabel } from 'further-ui/utils';
import { capitalize, lowerCase, sortBy } from 'lodash';
import { Box, Button, Divider, Tooltip } from '@mui/material';
import { CompareArrows, ExpandLess, ExpandMore } from '@mui/icons-material';
import { displayFeeAccountingType } from 'utils/displayFeeAccounting';
import { Api, FeeCalculationBasis, FeeType } from 'further-types/investment';
import useStyles from './styles';
import { FeeCalculationBasisLabels } from 'further-ui/labels';
import FeePercentageContainer from './FeePercentageContainer';
import { FeeTotals } from './FeeTotals';

type Fee = Api.GetInvestmentFeesResponse['fees'][number] & {
  isTransferred?: boolean;
};

type Props = {
  fees: Array<Fee>;
  loading: boolean;
};

const FutureFees: React.FC<Props> = ({ fees, loading }) => {
  const { classes } = useStyles();
  const [showFees, setShowFees] = useState(false);

  const columns = [
    {
      label: 'Date',
      key: 'date',
      render: (fee: Fee) => dateToLabel(fee.date),
    },
    {
      label: 'Fee type',
      key: 'feeType',
      render: (fee: Fee) => capitalize(lowerCase(fee.feeType ?? undefined)),
    },
    {
      label: 'Fee name',
      key: 'name',
      render: (fee: Fee) => {
        if (!fee.sourceFee && !fee.isTransferred) return <>{fee.name}</>;

        const tooltipText = [
          ...(fee.sourceFee
            ? [
                `${numberToCurrencyString(
                  fee.feeIncVAT,
                )} was transferred from ${
                  fee.sourceFee.investor.fullName
                } on ${dateToLabel(fee.sourceFee.investmentDate)}`,
              ]
            : []),
          ...(fee.isTransferred
            ? [
                `${numberToCurrencyString(fee.feeIncVAT)} was transferred to ${
                  fee.investor.fullName
                } on ${dateToLabel(fee.investmentDate)}`,
              ]
            : []),
        ].join(' ');

        return (
          <>
            <Tooltip title={tooltipText}>
              <Box alignItems="center" display="flex" gap={4}>
                {fee.name} <CompareArrows fontSize="small" />
              </Box>
            </Tooltip>
          </>
        );
      },
    },
    {
      label: 'Fee %',
      key: 'grossFeePercentage',
      render: (fee: Fee) => {
        if (fee.feeType === FeeType.Transaction) {
          return `-`;
        }

        return <FeePercentageContainer fee={fee} />;
      },
    },
    {
      label: 'Fee £ (Net)',
      key: 'feeExVAT',
      render: (fee: Fee) => numberToCurrencyString(fee.feeExVAT),
    },
    {
      label: 'Fee £ (Gross)',
      key: 'feeIncVAT',
      render: (fee: Fee) => numberToCurrencyString(fee.feeIncVAT),
    },
    {
      label: 'Frequency',
      key: 'chargingFrequency',
      render: (fee: Fee) =>
        capitalize(lowerCase(fee.chargingFrequency ?? undefined)),
    },
    {
      label: 'Fee basis',
      key: 'feeCalculationBasis',
      render: (fee: Fee) => {
        return fee.feeType === FeeType.Recurring
          ? FeeCalculationBasisLabels[
              fee.feeCalculationBasis ||
                FeeCalculationBasis.PercentageOfTotalCommitment
            ]
          : '';
      },
    },
    {
      label: 'Starting period',
      key: 'startingPeriod',
    },
    {
      label: 'Finishing period',
      key: 'finishingPeriod',
    },
    {
      label: 'Fee accounting',
      key: 'feeAccounting',
      render: (fee: Fee) =>
        displayFeeAccountingType(fee.feeAccounting, fee.viaExit),
    },
  ];

  const combinedFees = sortBy(fees, 'date').reduce<Array<Fee>>(
    (combined, fee) => {
      return [
        ...combined,
        fee,
        ...(fee.transferredFees ?? []).map((transferredFee) => ({
          ...transferredFee,
          isTransferred: true,
        })),
      ];
    },
    [],
  );

  return (
    <>
      <Button
        color="primary"
        className={classes.feeForecastsButton}
        onClick={() => {
          setShowFees(!showFees);
        }}
      >
        {`${showFees ? 'Hide' : 'Show'} fee forecast`}
        {showFees ? <ExpandLess /> : <ExpandMore />}
      </Button>
      {showFees && (
        <Table
          loading={loading}
          columns={columns.map((column) => ({
            ...column,
            getCellClassName: (fee: Fee) =>
              fee.isTransferred ? classes.inactiveCell : '',
          }))}
          variant="nohover"
          tablebody={combinedFees}
          TableFooter={
            <tfoot>
              <FeeTotals fees={combinedFees} columnCount={columns.length} />
            </tfoot>
          }
        />
      )}
      <Divider />
    </>
  );
};

export default FutureFees;
